import React, { useState } from "react";
import {
  Text,
  HStack,
  useTheme,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Flex,
  Tooltip,
} from "@chakra-ui/react";
import { TbEdit } from "react-icons/tb";
import { MdOutlineDeleteOutline, MdOutlineAssignment } from "react-icons/md";
import DeletePopup from "../../popups/DeletePopup";
import ChangeStatusPopup from "../../popups/ChangeStatusPopup";
import OpenFileModal from "../../modals/OpenFileModal/OpenFileModal";

const ScheduleClassTable = ({
  data,
  extraColumns = [],
  columnOrder = [],
  onEdit,
  onDelete,
  entityType,
  limit
}) => {
  const theme = useTheme();
  const [isDeletePopupOpen, setDeletePopupOpen] = useState(false);
  const [isStatusPopupOpen, setStatusPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFileModalOpen, setFileModalOpen] = useState(false);
  const [selectedOriFiles, setSelectedOriFiles] = useState([]);
  const [selectedTempFiles, setSelectedTempFiles] = useState([]);

  const { editButtonColor, deleteBtnColor, primaryBlueLight } =
    theme.colors.pallete;

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = limit;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = data.slice(startIndex, endIndex);
  const totalPages = Math.ceil(data.length / itemsPerPage);

  const fixedColumns = [
    { label: "S.No", key: "sno" },
    { label: "Subject", key: "subjectName" },
    { label: "Date", key: "scheduledDate" },
    { label: "From", key: "scheduledStartTime" },
    { label: "To", key: "scheduledEndTime" },
    { label: "Jee Year", key: "year" },
    { label: "Class Level", key: "classLevel" },
    { label: "Chapter", key: "chapterName" },
    { label: "Topic", key: "topicName" },
    { label: "Course Type", key: "classType" },
    { label: "Lecture", key: "lectureNo" },
    { label: "Agenda", key: "agenda" },
    { label: "Description", key: "description" },
    {
      label: "Uploaded Files",
      key: "files",
      render: (row) => {
        const fileCount = Array.isArray(row?.liveClassRoomFiles)
          ? row?.liveClassRoomFiles.length
          : 0;

        return fileCount === 0 ? (
          <Text fontSize="14px" fontWeight="500" color={primaryBlueLight}>
            No files
          </Text>
        ) : (
          <HStack
            cursor="pointer"
            color={primaryBlueLight}
            onClick={() => {
              setSelectedOriFiles(row?.liveClassRoomFiles || []);
              setSelectedTempFiles(row.files || []);
              setFileModalOpen(true);
            }}
          >
            <MdOutlineAssignment size={20} />
            <Text fontSize="12px" fontWeight="500">
              {`Open File${fileCount > 1 ? ` +${fileCount - 1}` : ""}`}
            </Text>
          </HStack>
        );
      },
    },
  ];

  let allColumns = [...fixedColumns, ...extraColumns];

  if (columnOrder.length > 0) {
    allColumns = columnOrder
      .map((key) => allColumns.find((col) => col.key === key))
      .filter(Boolean);
  }

  const handleDeleteClick = (row) => {
    setSelectedRow(row);
    setDeletePopupOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedRow) {
      onDelete(selectedRow.id);
      setDeletePopupOpen(false);
    }
  };

  return (
    <Box overflowX="auto" mt={6}>
      <Table variant="unstyled">
        <Thead>
          <Tr>
            {allColumns.map((col, index) => (
              <Th whiteSpace={"nowrap"} key={index}>
                {col.label}
              </Th>
            ))}
            <Th>Action</Th>
          </Tr>
        </Thead>
        <Tbody>
          {paginatedData.map((row, rowIndex) => (
            <Tr key={rowIndex}>
              {allColumns.map((col, colIndex) => (
                <Td key={colIndex}>
                  {col.render ? col.render(row) : row[col.key] || "—"}
                </Td>
              ))}
              <Td>
                <Flex>
                  <Tooltip
                    label="Edit is not available for completed classes"
                    isDisabled={entityType !== "class"}
                    hasArrow
                    placement="left"
                  >
                    <Button
                      onClick={() => onEdit(row.id)}
                      color={editButtonColor}
                      variant="ghost"
                      isDisabled={entityType === "class"}
                      p={0}
                    >
                      <TbEdit size={20} />
                    </Button>
                  </Tooltip>
                  <Tooltip
                    label="Delete is not available for completed classes"
                    isDisabled={entityType !== "class"}
                    hasArrow
                    placement="left"
                  >
                    <Button
                      onClick={() => handleDeleteClick(row)}
                      color={deleteBtnColor}
                      variant="ghost"
                      isDisabled={entityType === "class"}
                      p={0}
                    >
                      <MdOutlineDeleteOutline size={20} />
                    </Button>
                  </Tooltip>
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      <Flex justify="center" align="center" m={10} gap={4}>
        <Button
          size="sm"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          isDisabled={currentPage === 1}
        >
          Prev
        </Button>

        <Text fontSize="sm" fontWeight="medium">
          {currentPage} / {totalPages}
        </Text>

        <Button
          size="sm"
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          isDisabled={currentPage === totalPages}
        >
          Next
        </Button>
      </Flex>

      <DeletePopup
        isOpen={isDeletePopupOpen}
        onClose={() => setDeletePopupOpen(false)}
        onClick={handleConfirmDelete}
        text={`the ${entityType}`}
      />

      <ChangeStatusPopup
        isOpen={isStatusPopupOpen}
        onClose={() => setStatusPopupOpen(false)}
        onClick={handleConfirmDelete}
        text={` ${entityType}`}
      />

      <OpenFileModal
        isOpen={isFileModalOpen}
        onClose={() => setFileModalOpen(false)}
        tempfiles={selectedTempFiles}
        orifiles={selectedOriFiles}
        entity={entityType}
        docType={"live"}
      />
    </Box>
  );
};

export default ScheduleClassTable;
